import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, Loader2, AlertCircle, Files } from 'lucide-react';
import { useUpload } from '../contexts/UploadContext';

// Floating status pill for the *bulk* upload flow. Reads the single
// active session from UploadContext and renders a compact progress
// summary at the bottom of the viewport when:
//   1. There IS an active session, AND
//   2. The current route is NOT the one that started the upload
//      (otherwise FileUploader is showing the full inline progress
//      card, which is a better view — no need to duplicate it).
//
// The floating bar is bulk-native: "N of M files · X.X MB/s · 47%"
// rather than the old single-filename pattern.

const humanBytes = (n: number): string => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
};

const FloatingUploadBar: React.FC = () => {
  const { session, dismiss } = useUpload();
  const location = useLocation();

  if (!session) return null;
  // Hide when the user is on the page that started the upload — the
  // inline card there is already showing the same information (and
  // more, e.g. per-file results after completion).
  if (session.originPath === location.pathname) return null;

  const pct = session.progress
    ? Math.min(1, session.progress.bytesUploaded / Math.max(1, session.progress.totalBytes))
    : session.hashProgress
    ? session.hashProgress.hashed / Math.max(1, session.hashProgress.total)
    : 0;

  const status = (() => {
    if (session.phase === 'hashing') {
      const hp = session.hashProgress;
      return hp
        ? `Checking for duplicates · ${hp.hashed}/${hp.total}`
        : 'Checking for duplicates…';
    }
    if (session.phase === 'uploading') {
      const p = session.progress;
      if (!p) return 'Uploading…';
      const elapsed = (Date.now() - session.startedAt) / 1000;
      const rate = elapsed > 0.5 ? `${humanBytes(p.bytesUploaded / elapsed)}/s` : '…';
      return `${p.done}/${p.total} files · ${humanBytes(p.bytesUploaded)}/${humanBytes(p.totalBytes)} · ${rate}`;
    }
    if (session.phase === 'done') {
      const p = session.progress;
      const done = p?.done ?? session.totalFiles;
      const skipped = p?.skipped ?? 0;
      return skipped > 0
        ? `Uploaded ${done - skipped} · ${skipped} already present`
        : `Uploaded ${done} file${done === 1 ? '' : 's'}`;
    }
    return session.error ?? 'Upload failed';
  })();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 120, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 120, opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none"
      >
        <div className="mx-auto max-w-3xl px-4 pb-4 pointer-events-auto">
          <div className="relative rounded-2xl bg-space-900/85 border border-white/10 backdrop-blur-md shadow-2xl overflow-hidden">
            {/* Progress ribbon */}
            {(session.phase === 'uploading' || session.phase === 'hashing') && (
              <div className="h-1 bg-white/10">
                <div
                  className="h-full bg-gradient-to-r from-nebula-400 to-aurora-400 transition-all"
                  style={{ width: `${Math.max(2, pct * 100)}%` }}
                />
              </div>
            )}
            {session.phase === 'done' && <div className="h-1 bg-emerald-500/60" />}
            {session.phase === 'error' && <div className="h-1 bg-red-500/70" />}

            <div className="flex items-center gap-4 px-5 py-4">
              <div className="shrink-0">
                {session.phase === 'hashing' || session.phase === 'uploading' ? (
                  <Loader2 className="h-5 w-5 text-nebula-300 animate-spin" />
                ) : session.phase === 'done' ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-300" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-300" />
                )}
              </div>

              <Files className="h-5 w-5 text-white/40 shrink-0 hidden md:block" />

              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white/90 truncate">
                  <Link
                    to={session.originPath}
                    className="hover:text-white transition-colors"
                    title="Back to upload page"
                  >
                    {session.collectionId}
                  </Link>
                </div>
                <div className="text-xs text-white/55 mt-0.5 truncate">{status}</div>
              </div>

              {(session.phase === 'done' || session.phase === 'error') && (
                <button
                  onClick={dismiss}
                  className="shrink-0 p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                  aria-label="Dismiss"
                  title="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FloatingUploadBar;
